import { useState } from "react";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, ImageOff, RefreshCw } from "lucide-react";

/** Image URL for a poster record: the CDN copy when it has one, else the API preview. */
export function posterSrc(p) {
  return p.poster_url || `${API}/posters/${p.id}/preview`;
}

/** Square thumbnail that opens the poster full-size when clicked. */
export function PosterThumb({ poster, onOpen, className = "w-14 h-14" }) {
  if (poster.file_deleted) {
    return (
      <div className={`${className} rounded-md bg-slate-100 grid place-items-center text-slate-300`} title="File removed">
        <ImageOff className="w-5 h-5" />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(poster)}
      className={`${className} rounded-md overflow-hidden bg-slate-100 ring-1 ring-slate-200 hover:ring-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 transition`}
      title="View poster"
    >
      <img src={posterSrc(poster)} alt={poster.doctor_name} loading="lazy" className="w-full h-full object-cover object-top" />
    </button>
  );
}

/**
 * Full-size poster viewer. `poster` is the record to show (null = closed).
 * `onDownload(poster)` is the page's existing download handler.
 */
export default function PosterLightbox({ poster, onClose, onDownload, downloading }) {
  const [loaded, setLoaded] = useState(false);
  const open = !!poster;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setLoaded(false); } }}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-slate-950 border-slate-800">
        {poster && (
          <>
            <DialogTitle className="sr-only">{poster.doctor_name}</DialogTitle>
            <DialogDescription className="sr-only">Generated poster preview</DialogDescription>
            <div className="relative bg-slate-950 flex items-center justify-center" style={{ minHeight: 320 }}>
              {!loaded && (
                <div className="absolute inset-0 grid place-items-center text-slate-500">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                </div>
              )}
              <img
                src={posterSrc(poster)}
                alt={poster.doctor_name}
                onLoad={() => setLoaded(true)}
                className="max-h-[75vh] w-auto max-w-full object-contain"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-slate-900 text-slate-100">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{poster.doctor_name}</div>
                <div className="text-xs text-slate-400 truncate">
                  {[poster.qualification, poster.hospital].filter(Boolean).join(" · ")}
                  {poster.created_at && <> · {new Date(poster.created_at).toLocaleString()}</>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {poster.poster_url && (
                  <Button asChild size="sm" variant="outline" className="h-9 gap-1.5 bg-transparent border-slate-600 text-slate-100 hover:bg-slate-800 hover:text-white">
                    <a href={poster.poster_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" /> Open
                    </a>
                  </Button>
                )}
                {onDownload && (
                  <Button size="sm" className="h-9 gap-1.5 bg-brand-600 hover:bg-brand-700" onClick={() => onDownload(poster)} disabled={!!downloading}>
                    {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
